import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Table, Button, Spinner, Badge, Form, InputGroup, Pagination, Dropdown, Row, Col, Modal, Alert, ListGroup } from 'react-bootstrap';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaSearch, FaFilter, FaEllipsisV, FaCheckCircle, FaTimesCircle, FaEye, FaUserPlus,
         FaUser, FaEnvelope, FaPhone, FaPlus, FaChartLine, FaCreditCard, FaHome, FaMoneyBillWave,
         FaFileUpload, FaDownload, FaTimes, FaSignOutAlt, FaBell, FaQuestionCircle } from 'react-icons/fa';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { agentApi, productApi, notificationApi } from '../../services/api'; // Ensure productApi is here
import { useAuth } from '../../hooks/useAuth';
import AgentSidebar from '../../components/AgentSidebar';
import AddLeadForm from '../../components/Leads/AddLeadForm'; // Import the reusable form
import BulkUploadModal from '../../components/Leads/BulkUploadModal'; // Import the bulk upload modal
import './Leads.css';

// Case status mapping
const caseStatusMap = {
  1: { label: 'Fresh', variant: 'primary' },
  2: { label: 'Product selected', variant: 'info' },
  3: { label: 'Personal Details', variant: 'info' },
  4: { label: 'Residential Details', variant: 'info' },
  5: { label: 'Professional Details', variant: 'info' },
  6: { label: 'Doc Uploaded', variant: 'info' },
  7: { label: 'Pushed to Back Office', variant: 'warning' },
  8: { label: 'OTP Verified', variant: 'success' },
  9: { label: 'Bank Application Submitted', variant: 'info' },
  10: { label: 'In process', variant: 'secondary' },
  11: { label: 'Rejected', variant: 'danger' },
  12: { label: 'Approved', variant: 'success' },
  13: { label: 'Disbursed', variant: 'success' },
  14: { label: 'Not Interested', variant: 'danger' }
};

// Case type mapping
const caseTypeMap = {
  'Credit Card': 'Credit Card',
  'Home Loan': 'Home Loan',
  'Personal Loan': 'Personal Loan',
  'Business Loan': 'Business Loan'
};

function AgentLeads() {
  const navigate = useNavigate();
  const { getCurrentUser, logout } = useAuth();
  const currentUser = getCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [totalLeads, setTotalLeads] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [
    { value: 5, label: '5 rows' },
    { value: 10, label: '10 rows' },
    { value: 25, label: '25 rows' },
    { value: 50, label: '50 rows' },
  ];
  
  // States for add lead modal
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  // Removed old form state: newLead, validated, addLeadError, addLeadLoading

  // Add notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);

  
  // Notification handlers
  const toggleNotifications = () => {
    setShowNotifications(prev => !prev);
  };
  
  const handleMarkAsRead = async (notificationId) => {
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification || notification.is_read) return;

    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  
  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;

    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };
  
  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch (e) { return timestamp; }
  };

  useEffect(() => {    
    fetchLeads();
    fetchProducts(); // Fetch products for the add lead modal
  }, [searchParams, currentPage, pageSize, searchTerm, statusFilter]);
  
  // Notifications polling effect
  useEffect(() => {
    let intervalId;
    const fetchNotificationsData = async () => {
      try {
        const countResponse = await notificationApi.getUnreadCount();
        setUnreadCount(countResponse.data.unread_count);
      } catch (error) {
        console.error('Error fetching notifications count:', error);
      }
    };

    fetchNotificationsData(); // Initial fetch
    intervalId = setInterval(fetchNotificationsData, 60000); // Poll count every minute
    
    return () => clearInterval(intervalId); // Cleanup
  }, []);

  // Fetch full notifications when dropdown opens
  useEffect(() => {
    const fetchFullNotifications = async () => {
      if (showNotifications) {
        setLoadingNotifications(true);
        try {
          const notificationsResponse = await notificationApi.getNotifications({ limit: 10 });
          setNotifications(notificationsResponse.data);
        } catch (error) {
          console.error('Error fetching notifications:', error);
          setNotifications([]);
        } finally {
          setLoadingNotifications(false);
        }
      }
    };
    fetchFullNotifications();
  }, [showNotifications]);

  // Handle clicks outside notification dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError('');
    // Build query parameters
    const params = {
      skip: (currentPage - 1) * pageSize,
      limit: pageSize,
    };
    
    if (searchTerm) {
      params.search = searchTerm;
    }
    
    if (statusFilter) {
      params.status = statusFilter;
    }
    
    try {
      const response = await agentApi.getLeads(params);
      console.log('Leads fetched:', response.data);
      setLeads(response.data.items || []);
      setTotalLeads(response.data.total || 0);
    } catch (err) {
      console.error('Error fetching leads:', err);
      setError('Failed to load leads. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, statusFilter]); // Add dependencies

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    // fetchLeads will be triggered by the effect hook when currentPage changes
  };

  const handleFilterChange = (filterType, selectedOption) => {
    if (filterType === 'status') {
      const value = selectedOption ? selectedOption.value : '';
      setStatusFilter(value);
    }
    
    // Reset to first page when changing filters
    setCurrentPage(1);
    // fetchLeads will be triggered by the effect hook when the dependencies change
  };

  // Function to manually trigger a refresh of the leads data
  const refreshLeads = () => {
    fetchLeads();
  };

  const fetchProducts = useCallback(async (categoryId = null) => { // Add useCallback and categoryId
    setLoadingProducts(true);
    try {
      const params = { limit: 500 }; // Fetch a large number for the dropdown
      if (categoryId !== null) {
        params.category_id = categoryId;
      }
      // Agent might only see products assigned to them - adjust API call if needed
      // params.agent_id = currentUser?.profile?.id; // Example if API supports filtering by agent

      const response = await productApi.getProducts(params); // Pass params
      setProducts(response.data.items || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      setProducts([]); // Clear products on error
    } finally {
      setLoadingProducts(false);
    }
  }, [currentUser?.profile?.id]); // Add currentUser dependency if API call uses it

  // Removed old handlers: handleNewLeadChange, handleSelectChange, handleAddLead

  // New handler for AddLeadForm submission
  const handleAddLeadSubmit = useCallback(async (leadData) => {
    // The agent ID is fixed for this page, taken from the current user
    // The AddLeadForm passes back the core leadData, agentId is handled here.
    try {
      console.log('Creating lead via Agent/Leads for agent ID:', currentUser?.profile?.id, 'with data:', leadData);
      // Use the agentApi.addLead endpoint (assuming it exists and takes leadData)
      await agentApi.addLead(leadData);

      setShowAddLeadModal(false); // Close modal on success

      // Show success message
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Lead created successfully',
        timer: 2500,
        showConfirmButton: false
      });

      // Refresh leads list
      fetchLeads(); // Ensure fetchLeads is accessible here

    } catch (err) {
      console.error('Error creating lead from Agent/Leads:', err);
      // Re-throw the error so AddLeadForm can display it within the modal
      throw err;
    }
  }, [currentUser?.profile?.id, fetchLeads]); // Depend on currentUser and fetchLeads
  
  // Handler for bulk upload submission
  const handleBulkUploadSubmit = useCallback(async (file) => {
    console.log('Submitting bulk upload for agent');
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const agentId = currentUser?.agent_profile?.id || currentUser?.id;
      if (agentId) {
        formData.append('agent_id', agentId);
      }
      const response = await agentApi.bulkUploadLeads(formData);
      // Close modal on success
      setShowBulkUploadModal(false);
      // Refresh leads list
      fetchLeads();
      // Show success message with counts (no timer, requires user confirmation)
      Swal.fire({
        icon: 'success',
        title: 'Bulk Upload Successful',
        text: `Successfully processed ${response.data.total_records} leads: ${response.data.successful} created, ${response.data.duplicates} duplicates, ${response.data.skipped} skipped, ${response.data.failed} failed.`,
        showConfirmButton: true,
        confirmButtonText: 'OK'
      });
    } catch (err) {
      console.error('Error in bulk upload:', err);
      throw new Error(err.response?.data?.detail || err.message || 'Bulk upload failed.');
    }
  }, [currentUser, fetchLeads]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  
  const handlePageSizeChange = (selectedOption) => {
    const newPageSize = selectedOption.value;
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
    // fetchLeads will be triggered by the effect hook
  };

  const handleViewLead = (leadId) => {
    // Navigate to lead details page
    navigate(`/agent/lead/${leadId}`);
  };

  // Function to determine badge color based on CIBIL score
  const getCibilScoreBadgeColor = (score) => {
    if (!score) return 'secondary';
    if (score >= 750) return 'success';
    if (score >= 650) return 'info';
    if (score >= 550) return 'warning';
    return 'danger';
  };

  // Handle checking CIBIL for a lead
  const handleCheckCibilRequest = async (leadId) => {
    try {
      Swal.fire({
        title: 'Checking CIBIL Score',
        text: 'Please wait while we fetch the CIBIL data...',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      
      const response = await agentApi.requestCibilCheck(leadId);
      
      if (response.data.success) {
        if (response.data.cibil_id){
          Swal.fire({
            icon: 'success',
            title: 'CIBIL Check Successful',
            text: `CIBIL Score: ${response.data.cibil_score}`,
            confirmButtonText: 'View Details',
            showCancelButton: true,
            cancelButtonText: 'Stay on Leads'
          }).then((result) => {
            if (result.isConfirmed) {
              navigate(`/agent/cibil/${response.data.cibil_id}`);
            } else {
              fetchLeads();
            }
          });
        }else{
          Swal.fire({
          icon: 'success',
          title: 'SMS sent to user',
          text: `We have sent an SMS to user to check their CIBIL Score`,
          confirmButtonText: 'View Details',
          showCancelButton: true,
          cancelButtonText: 'Stay on Leads'
        }).then((result) => {
          if (result.isConfirmed) {
            navigate(`/agent/cibil/${response.data.cibil_id}`);
          } else {
            fetchLeads();
          }
        });
        }
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'CIBIL Check Failed',
          text: response.data.message || 'Unable to fetch CIBIL data for this lead'
        });
      }
    } catch (error) {
      console.error('Error checking CIBIL:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response && error.response.data && error.response.data.detail ? error.response.data.detail : 'Failed to check CIBIL score. Please try again.'
      });
    }
  };

  const renderPagination = () => {
    const totalPages = Math.ceil(totalLeads / pageSize);
    if (totalPages <= 1 && totalLeads <= pageSize) return null;

    const startRecord = (currentPage - 1) * pageSize + 1;
    const endRecord = Math.min(currentPage * pageSize, totalLeads);

    const pageButtons = [];
    const maxButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(startPage + maxButtons - 1, totalPages);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    for (let number = startPage; number <= endPage; number++) {
      pageButtons.push(
        <Pagination.Item 
          key={number} 
          active={number === currentPage}
          onClick={() => handlePageChange(number)}
          className={number === currentPage ? 'active' : ''}
        >
          {number}
        </Pagination.Item>
      );
    }

    return  (
      <div className="dso-pagination-container">
        <div className="dso-pagination-info-bar">
          <div className="dso-pagination-info">
            Showing {startRecord} to {endRecord} of {totalLeads} records
          </div>
          
          <div className="dso-pagination-pagesize">
            <span className="me-2">Rows per page:</span>
            <Select
              className="dso-pagesize-select"
              classNamePrefix="react-select"
              options={pageSizeOptions}
              value={pageSizeOptions.find(option => option.value === pageSize)}
              onChange={handlePageSizeChange}
              isSearchable={false}
              menuPlacement="top"
            />
          </div>
        </div>
        
        <div className="dso-pagination-controls">
          <Pagination>
            <Pagination.First 
              onClick={() => handlePageChange(1)} 
              disabled={currentPage === 1}
              className="pagination-first"
            />
            <Pagination.Prev 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1}
              className="pagination-prev"
            />
            
            {startPage > 1 && (
              <>
                <Pagination.Item onClick={() => handlePageChange(1)}>1</Pagination.Item>
                {startPage > 2 && <Pagination.Ellipsis />}
              </>
            )}
            
            {pageButtons}
            
            {endPage < totalPages && (
              <>
                {endPage < totalPages - 1 && <Pagination.Ellipsis />}
                <Pagination.Item onClick={() => handlePageChange(totalPages)}>
                  {totalPages}
                </Pagination.Item>
              </>
            )}
            
            <Pagination.Next 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages}
              className="pagination-next"
            />
            <Pagination.Last 
              onClick={() => handlePageChange(totalPages)} 
              disabled={currentPage === totalPages}
              className="pagination-last"
            />
          </Pagination>
        </div>
      </div>
    );
  };

  return (
    <div className="agent-dashboard-layout">
      {/* Sidebar */}
      <AgentSidebar />
      
      {/* Main Content */}
      <div className="agent-content-wrapper">
        {/* Header */}
        <header className="agent-header">
          <div className="agent-greeting">
            <span className="agent-greeting-text">Hi, {currentUser?.name || 'Agent'} <span className="agent-greeting-emoji">👋</span></span>
          </div>

          <div className="agent-header-search">
            <div className="search-input-container">
              <FaSearch className="search-icon" />
              <input 
                type="text" 
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch(e);
                  }
                }}
              />
              {searchTerm && (
                <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                  <FaTimes />
                </button>
              )}
            </div>
          </div>

          <div className="agent-header-actions">
            <div className="agent-header-icon">
              <FaQuestionCircle size={18} />
            </div>
            
            <div ref={notificationRef} className="position-relative agent-notifications me-3">
              <Button
                variant="link"
                className="text-secondary p-0 position-relative"
                onClick={toggleNotifications}
                id="dropdown-notifications-agent"
                aria-expanded={showNotifications}
                title="Notifications"
              >
                <FaBell size={20} />
                {unreadCount > 0 && (
                  <Badge
                    pill
                    bg="danger"
                    className="position-absolute top-0 start-100 translate-middle border border-light"
                    style={{ fontSize: '0.65em', padding: '0.3em 0.5em' }}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                    <span className="visually-hidden">unread notifications</span>
                  </Badge>
                )}
              </Button>

              {/* Notification Dropdown Menu */}
              <Dropdown.Menu
                show={showNotifications}
                className="shadow mt-2 notification-dropdown"
                style={{ minWidth: '350px', maxHeight: '450px', overflowY: 'auto' }}
              >
                <Dropdown.Header className="d-flex justify-content-between align-items-center">
                  Notifications
                  {unreadCount > 0 && (
                    <Button variant="link" size="sm" onClick={handleMarkAllAsRead} className="p-0 text-decoration-none">
                      Mark all read
                    </Button>
                  )}
                </Dropdown.Header>
                <Dropdown.Divider className="my-1" />
                {loadingNotifications ? (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" variant="primary" /> Loading...
                  </div>
                ) : notifications.length > 0 ? (
                  <ListGroup variant="flush">
                    {notifications.map(notif => (
                      <ListGroup.Item
                        key={notif.id}
                        action={!notif.is_read}
                        onClick={() => handleMarkAsRead(notif.id)}
                        style={{
                          cursor: !notif.is_read ? 'pointer' : 'default',
                          backgroundColor: !notif.is_read ? '#f8f9fa' : 'transparent',
                          borderLeft: !notif.is_read ? '4px solid #0d6efd' : 'none',
                          padding: '0.75rem 1.25rem',
                          borderBottom: '1px solid #eee'
                        }}
                        className="notification-item"
                      >
                        <div className={`fw-bold ${!notif.is_read ? 'text-primary' : ''}`}>{notif.title || 'Notification'}</div>
                        <div className="text-muted small mb-1">{notif.message || 'No details available.'}</div>
                        <small className="text-muted">{formatTimestamp(notif.created_at)}</small>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                ) : (
                  <Dropdown.ItemText className="text-center text-muted p-3">
                    You're all caught up!
                  </Dropdown.ItemText>
                )}
              </Dropdown.Menu>
            </div>

            <div className="agent-user-profile">
              <div className="agent-avatar">
                <FaUser />
              </div>
              <div className="agent-user-info">
                <span className="agent-user-name">{currentUser?.name || 'Agent User'}</span>
                <button className="agent-logout-btn" onClick={() => {
                  logout();
                  navigate('/login');
                }}>
                  <FaSignOutAlt size={14} />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          </div>
        </header>
        
        {/* Main Content */}
        <div className="agent-main-content">
          <div className="dso-leads-container">
            <div className="dso-leads-header">
              <h3>Leads</h3>
              <div className="dso-leads-actions">
                <Button 
                  variant="primary"
                  onClick={() => setShowAddLeadModal(true)}
                  className="me-2"
                >
                  <FaUserPlus className="me-1"/> Add Lead
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setShowBulkUploadModal(true)}
                >
                  <FaFileUpload className="me-1"/> Bulk Upload
                </Button>
              </div>
            </div>

      <Row className="dso-leads-filters-row align-items-center">
        <Col xs={12} md={6}>
          <Form onSubmit={handleSearch} className="dso-search-form">
            <InputGroup size="sm">
              <Form.Control
                placeholder="Search name, phone, or application #"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button variant="outline-secondary" type="submit">
                <FaSearch />
              </Button>
            </InputGroup>
          </Form>
        </Col>
        
        <Col xs={12} md={6} className="d-flex justify-content-md-end mt-3 mt-md-0">
          <div className="dso-filter-group">
            <div className="dso-select-container me-2">
              <label className="dso-filter-label">Status:</label>
              <Select
                className="dso-select"
                classNamePrefix="react-select"
                placeholder="All Statuses"
                isClearable
                isSearchable={false}
                value={statusFilter ? { label: caseStatusMap[statusFilter]?.label, value: statusFilter } : null}
                onChange={(selectedOption) => handleFilterChange('status', selectedOption)}
                options={[
                  { value: '', label: 'All' },
                  ...Object.entries(caseStatusMap).map(([key, { label }]) => ({
                    value: key,
                    label: label
                  }))
                ]}
              />
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <div className="alert alert-danger mt-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="dso-leads-loading">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          {leads.length === 0 ? (
            <div className="dso-leads-empty">
              <p>No leads found with the current filters.</p>
            </div>
          ) : (
            <>
              <div className="dso-leads-table-container">
                <Table responsive striped className="dso-leads-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Product</th>
                      <th>Status</th>
                      <th>CIBIL</th>
                      <th>Application #</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => {
                      // Get user data regardless of structure
                      const userName = lead.user ? lead.user.name : (lead.lead_user ? lead.lead_user.name : 'Unknown');
                      const userPhone = lead.user ? lead.user.mobile : (lead.lead_user ? lead.lead_user.phone || lead.lead_user.mobile : 'Unknown');
                      
                      // Get CIBIL score - check all possible locations
                      const cibilScore = lead.cibil_score || 
                                        (lead.lead_user && lead.lead_user.cibil_score) || 
                                        (lead.user && lead.user.cibil_score);
                      
                      return (
                        <tr key={lead.id}>
                          <td>{userName}</td>
                          <td>{userPhone}</td>
                          <td>
                            <div className="dso-lead-product">
                              <span className="dso-lead-product-name">{lead.product?.name || 'N/A'}</span>
                              <span className="dso-lead-product-type">{caseTypeMap[lead.case_type] || lead.case_type}</span>
                            </div>
                          </td>
                          <td>
                            <Badge 
                              bg={caseStatusMap[lead.status?.code]?.variant || 'secondary'}
                              className="dso-lead-status"
                            >
                              {lead.status?.name}
                            </Badge>
                          </td>
                          <td>
                            {cibilScore ? (
                              <Badge 
                                bg={getCibilScoreBadgeColor(cibilScore)}
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate(`/agent/cibil/${lead.lead_user.cibil_id}`)}
                              >
                                {cibilScore}
                              </Badge>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="outline-secondary"
                                onClick={() => handleCheckCibilRequest(lead.id)}
                                disabled={lead.checking_cibil}
                              >
                                {lead.checking_cibil ? (
                                  <>
                                    <Spinner 
                                      as="span"
                                      animation="border"
                                      size="sm"
                                      role="status"
                                      aria-hidden="true"
                                      className="me-1"
                                    />
                                    Checking...
                                  </>
                                ) : (
                                  <>Check CIBIL</>
                                )}
                              </Button>
                            )}
                          </td>
                          <td>
                            {lead.application_no || 'N/A'}
                          </td>
                          <td>
                            <div>
                              <Button 
                                variant="outline-primary" 
                                size="sm" 
                                className="me-2"
                                onClick={() => handleViewLead(lead.lead_profile.id)}
                              >
                                <FaEye /> View
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
              
              {renderPagination()}
            </>
          )}
        </>
      )}
    </div> {/* Closing tag for dso-leads-container */}

    {/* Render the AddLeadForm component */}
    <AddLeadForm
        isOpen={showAddLeadModal}
        onClose={() => setShowAddLeadModal(false)}
        onSubmit={handleAddLeadSubmit} // Use the new handler
        products={products} // Pass products state
        loadingProducts={loadingProducts} // Pass loading state
        fetchProducts={fetchProducts} // Pass the fetch function
        initialAgentId={currentUser?.id?.toString()}
    />

    {/* Render the BulkUploadModal component */}
    <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSubmit={handleBulkUploadSubmit}
        fixedAgentId={currentUser?.agent_profile?.id || currentUser?.id}
        fixedAgentName={currentUser?.name}
    />

    </div> {/* Closing tag for agent-main-content */}
    </div> {/* Closing tag for agent-content-wrapper */}
   </div> // Closing tag for agent-dashboard-layout
  );
}

export default AgentLeads;
